/*
  CrabJuice VAT toggle (2026-07)
  Lets shoppers choose incl./excl. VAT price display, per selected EU country.
  Relies on:
    - window.CJ_VAT config object (injected in theme.liquid), including an
      i18n sub-object with all translated UI copy for the modal + header
      toggle, resolved server-side from request.locale.iso_code (with a
      Swedish market/country override - see theme.liquid for details), plus
      a top-level `lang` field (sv/en/de/es/fr/it/nl) used here to pick the
      matching country-name dictionary for the country <select>, and a top-
      level `infoUrl` pointing at the "Understanding VAT" page linked from the
      popup's "What is this?" link (see applyI18n below).
    - assets/vat-rates.json (country -> standard VAT rate table)
    - data-raw-price / data-raw-compare-price / data-money-format attributes
      added to snippets/price.liquid, sections/main-cart-items.liquid and
      snippets/cart-drawer.liquid
    - Zipify OCU's own cart drawer markup (.ocu-cart-line-item-wrapper[data-key],
      .ocu-cart-line-price-now, .ocu-estimated-info__value) - OCU is a 3rd-party
      app we don't control the Liquid for, so instead we fetch /cart.js (Shopify's
      standard AJAX cart endpoint) and match rows by the cart line's `key`, which
      OCU conveniently exposes as data-key on each line wrapper.
    - OCU's "You may also like" upsell widget (.ocu-widget) - these are recommended
      products NOT yet in the cart, so there's no /cart.js data for them. OCU renders
      these at a fixed, always tax-EXCLUSIVE price regardless of the shop's tax-inclusive
      setting (confirmed: exactly price/1.25 vs the real SE-inclusive price everywhere
      else) - this was the original "OCU shows prices without VAT" complaint. Since we
      can't get raw cents for these, we treat OCU's own rendered number as the excl-VAT
      baseline (parsed once, cached in a data attribute) and only need to multiply it
      up for the incl-VAT case.
    - .cj-vat-tax-note span(s) in sections/main-product.liquid - Dawn's standard
      "Taxes included" note under the PDP price. It's always server-rendered (since
      the shop's cart.taxes_included is always true), which contradicts the page when
      a customer has chosen to view exclusive-of-VAT prices, so we hide it in that case.
    - OCU's Reward Bar (.ocu-reward-bar) free-shipping/discount progress message. We do
      NOT rewrite OCU's own text here (tried 2026-07-20, reverted - see project memory),
      because it broke OCU's own Vue reactivity and froze incorrect text permanently.
      Instead we insert our OWN separate sibling note (.cj-vat-reward-note) next to it,
      recomputed fresh every cycle from the reward bar's live fill-percentage (basis-
      independent) plus the real /cart.js total - never touches OCU's own node, so it
      can never get stuck and never interferes with OCU's own reactivity.
    - Header VAT toggle - rendered TWICE (sections/header.liquid, inline in the
      desktop icons row; snippets/header-drawer.liquid, inside the mobile drawer's
      localization block) since Dawn already duplicates its own account/localization
      controls the same way between desktop header and mobile drawer rather than
      repositioning one shared element. Both instances share the `.cj-vat-header-toggle`
      class (no ids - see updatePillLabel below, which uses querySelectorAll so both
      stay in sync from a single choice) and are shown/hidden via Dawn's own
      `small-hide` / `medium-hide large-up-hide` utility classes. The popup modal
      (snippets/vat-toggle.liquid) is a single shared markup-only shell; all customer-
      facing text on both pill instances and the modal is populated here from CFG.i18n
      (see applyI18n / updatePillLabel below).
    - Third-party/independent widgets that render their OWN prices from raw variant
      cents (e.g. assets/bundle-builder.js, the Color Knobs mix-and-match widget) -
      these don't use the data-raw-price convention at all, so we expose a small
      public API on window.CJVatToggle (computeDisplay/formatMoney/getChoice) plus a
      'cj-vat:changed' DOM event fired whenever the customer changes their VAT choice
      OR whenever the initial rates/choice state has just been resolved on page load,
      so such widgets can convert their own numbers and re-render both on that first
      load AND on later changes, without vat-toggle.js needing to know anything about
      their specific markup.
*/
(function () {
  var CFG = window.CJ_VAT || {};
  var I18N = CFG.i18n || {};
  var STORAGE_CHOICE = 'cj_vat_choice';
  var STORAGE_COUNTRY = 'cj_vat_country';
  var rates = null;

  /* Country names for the popup's country <select>, one dictionary per resolved
     language (CFG.lang: sv/en/de/es/fr/it/nl - sv is the SE-market override, not a
     real Shopify locale). Not literal/machine translation - standard exonyms used on
     e-commerce sites in each language (e.g. "Germany"/"Allemagne"/"Deutschland"). */
  var COUNTRY_NAMES_BY_LANG = {
    sv: {
      AT: 'Österrike', BE: 'Belgien', BG: 'Bulgarien', HR: 'Kroatien', CY: 'Cypern',
      CZ: 'Tjeckien', DK: 'Danmark', EE: 'Estland', FI: 'Finland', FR: 'Frankrike',
      DE: 'Tyskland', GR: 'Grekland', HU: 'Ungern', IE: 'Irland', IT: 'Italien',
      LV: 'Lettland', LT: 'Litauen', LU: 'Luxemburg', MT: 'Malta', NL: 'Nederländerna',
      PL: 'Polen', PT: 'Portugal', RO: 'Rumänien', SK: 'Slovakien', SI: 'Slovenien',
      ES: 'Spanien', SE: 'Sverige'
    },
    en: {
      AT: 'Austria', BE: 'Belgium', BG: 'Bulgaria', HR: 'Croatia', CY: 'Cyprus',
      CZ: 'Czechia', DK: 'Denmark', EE: 'Estonia', FI: 'Finland', FR: 'France',
      DE: 'Germany', GR: 'Greece', HU: 'Hungary', IE: 'Ireland', IT: 'Italy',
      LV: 'Latvia', LT: 'Lithuania', LU: 'Luxembourg', MT: 'Malta', NL: 'Netherlands',
      PL: 'Poland', PT: 'Portugal', RO: 'Romania', SK: 'Slovakia', SI: 'Slovenia',
      ES: 'Spain', SE: 'Sweden'
    },
    de: {
      AT: 'Österreich', BE: 'Belgien', BG: 'Bulgarien', HR: 'Kroatien', CY: 'Zypern',
      CZ: 'Tschechien', DK: 'Dänemark', EE: 'Estland', FI: 'Finnland', FR: 'Frankreich',
      DE: 'Deutschland', GR: 'Griechenland', HU: 'Ungarn', IE: 'Irland', IT: 'Italien',
      LV: 'Lettland', LT: 'Litauen', LU: 'Luxemburg', MT: 'Malta', NL: 'Niederlande',
      PL: 'Polen', PT: 'Portugal', RO: 'Rumänien', SK: 'Slowakei', SI: 'Slowenien',
      ES: 'Spanien', SE: 'Schweden'
    },
    es: {
      AT: 'Austria', BE: 'Bélgica', BG: 'Bulgaria', HR: 'Croacia', CY: 'Chipre',
      CZ: 'República Checa', DK: 'Dinamarca', EE: 'Estonia', FI: 'Finlandia', FR: 'Francia',
      DE: 'Alemania', GR: 'Grecia', HU: 'Hungría', IE: 'Irlanda', IT: 'Italia',
      LV: 'Letonia', LT: 'Lituania', LU: 'Luxemburgo', MT: 'Malta', NL: 'Países Bajos',
      PL: 'Polonia', PT: 'Portugal', RO: 'Rumanía', SK: 'Eslovaquia', SI: 'Eslovenia',
      ES: 'España', SE: 'Suecia'
    },
    fr: {
      AT: 'Autriche', BE: 'Belgique', BG: 'Bulgarie', HR: 'Croatie', CY: 'Chypre',
      CZ: 'Tchéquie', DK: 'Danemark', EE: 'Estonie', FI: 'Finlande', FR: 'France',
      DE: 'Allemagne', GR: 'Grèce', HU: 'Hongrie', IE: 'Irlande', IT: 'Italie',
      LV: 'Lettonie', LT: 'Lituanie', LU: 'Luxembourg', MT: 'Malte', NL: 'Pays-Bas',
      PL: 'Pologne', PT: 'Portugal', RO: 'Roumanie', SK: 'Slovaquie', SI: 'Slovénie',
      ES: 'Espagne', SE: 'Suède'
    },
    it: {
      AT: 'Austria', BE: 'Belgio', BG: 'Bulgaria', HR: 'Croazia', CY: 'Cipro',
      CZ: 'Repubblica Ceca', DK: 'Danimarca', EE: 'Estonia', FI: 'Finlandia', FR: 'Francia',
      DE: 'Germania', GR: 'Grecia', HU: 'Ungheria', IE: 'Irlanda', IT: 'Italia',
      LV: 'Lettonia', LT: 'Lituania', LU: 'Lussemburgo', MT: 'Malta', NL: 'Paesi Bassi',
      PL: 'Polonia', PT: 'Portogallo', RO: 'Romania', SK: 'Slovacchia', SI: 'Slovenia',
      ES: 'Spagna', SE: 'Svezia'
    },
    nl: {
      AT: 'Oostenrijk', BE: 'België', BG: 'Bulgarije', HR: 'Kroatië', CY: 'Cyprus',
      CZ: 'Tsjechië', DK: 'Denemarken', EE: 'Estland', FI: 'Finland', FR: 'Frankrijk',
      DE: 'Duitsland', GR: 'Griekenland', HU: 'Hongarije', IE: 'Ierland', IT: 'Italië',
      LV: 'Letland', LT: 'Litouwen', LU: 'Luxemburg', MT: 'Malta', NL: 'Nederland',
      PL: 'Polen', PT: 'Portugal', RO: 'Roemenië', SK: 'Slowakije', SI: 'Slovenië',
      ES: 'Spanje', SE: 'Zweden'
    }
  };

  function getCountryNames() {
    return COUNTRY_NAMES_BY_LANG[CFG.lang] || COUNTRY_NAMES_BY_LANG.en;
  }

  function getRates() {
    if (rates) return Promise.resolve(rates);
    if (!CFG.ratesUrl) return Promise.resolve({});
    return fetch(CFG.ratesUrl)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        rates = data;
        return rates;
      })
      .catch(function () { return {}; });
  }

  function getChoice() {
    try { return localStorage.getItem(STORAGE_CHOICE); } catch (e) { return null; }
  }
  function getCountry() {
    try { return localStorage.getItem(STORAGE_COUNTRY) || CFG.defaultCountry || 'SE'; }
    catch (e) { return CFG.defaultCountry || 'SE'; }
  }
  function setPref(choice, country) {
    try {
      localStorage.setItem(STORAGE_CHOICE, choice);
      localStorage.setItem(STORAGE_COUNTRY, country);
    } catch (e) { /* localStorage unavailable, ignore */ }
  }

  /* Money formatting - supports Shopify's standard money_format placeholders */
  function formatMoney(cents, format) {
    format = format || CFG.moneyFormat || '{{amount}}';
    if (cents == null || isNaN(cents)) return '';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    var placeholderMatch = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var placeholder = placeholderMatch ? placeholderMatch[1] : 'amount';
    var value;
    switch (placeholder) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', '');
        break;
      case 'amount':
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  function getCurrentRate() {
    var country = getCountry();
    if (!rates || rates[country] === undefined || rates[country] === null) return null;
    return rates[country];
  }

  function computeDisplay(rawCents, rate) {
    var taxesIncluded = !!CFG.taxesIncluded;
    var choice = getChoice() || 'incl';
    if (taxesIncluded) {
      if (choice === 'excl') return Math.round(rawCents / (1 + rate / 100));
      return rawCents;
    }
    if (choice === 'incl') return Math.round(rawCents * (1 + rate / 100));
    return rawCents;
  }

  /* Public wrapper for external widgets (e.g. bundle-builder.js): converts a raw
     (shop, tax-inclusive) cents value using the CURRENTLY selected country's rate,
     falling back to returning rawCents unchanged if rates haven't loaded yet or the
     selected country has no known rate - same safe-no-op behavior as applyVatDisplay
     uses internally, so callers never need to null-check the rate themselves. */
  function publicComputeDisplay(rawCents) {
    var rate = getCurrentRate();
    if (rate == null) return rawCents;
    return computeDisplay(rawCents, rate);
  }

  function formatFor(el, cents) {
    var useCurrency = el.getAttribute('data-money-format') === 'with_currency';
    return formatMoney(cents, useCurrency ? CFG.moneyFormatWithCurrency : CFG.moneyFormat);
  }

  function rewriteDataPriceElements(rate) {
    document.querySelectorAll('[data-raw-price]').forEach(function (el) {
      var raw = parseInt(el.getAttribute('data-raw-price'), 10);
      if (isNaN(raw)) return;

      var priceItems = el.querySelectorAll(':scope > .price__container .price-item--regular, :scope > .price__container .price-item--sale');
      if (priceItems.length) {
        var displayValue = computeDisplay(raw, rate);
        var formatted = formatFor(el, displayValue);
        priceItems.forEach(function (span) { span.textContent = formatted; });

        var compareRaw = parseInt(el.getAttribute('data-raw-compare-price'), 10);
        if (compareRaw > 0) {
          var compareDisplay = computeDisplay(compareRaw, rate);
          var compareFormatted = formatFor(el, compareDisplay);
          el.querySelectorAll(':scope > .price__container s.price-item--regular').forEach(function (s) {
            s.textContent = compareFormatted;
          });
        }
      } else {
        var display = computeDisplay(raw, rate);
        el.textContent = formatFor(el, display);
      }
    });
  }

  /* Zipify OCU's own cart drawer line items - 3rd party markup, no data-raw-price hooks
     available. We fetch /cart.js and match rows by the line's `key`, which OCU exposes
     as data-key on .ocu-cart-line-item-wrapper. These are safe to overwrite every cycle
     because we always derive the value fresh from authoritative /cart.js data (no
     dependence on OCU's own wording/label), so even if this interferes with OCU's own
     reactivity for that node, we simply re-assert the correct value on the very next
     mutation cycle regardless. */
  var cartJsCache = null;
  var cartJsCacheTime = 0;
  function fetchCartJson() {
    var now = Date.now();
    if (cartJsCache && (now - cartJsCacheTime) < 800) return Promise.resolve(cartJsCache);
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        cartJsCache = data;
        cartJsCacheTime = Date.now();
        return data;
      })
      .catch(function () { return null; });
  }

  function setInnerText(container, selector, text) {
    var el = container.querySelector(selector);
    if (!el) return;
    var span = el.querySelector('span');
    if (span && span.children.length === 0) {
      span.textContent = text;
    } else {
      el.textContent = text;
    }
  }

  function applyOcuCartPrices(rate) {
    var wrappers = document.querySelectorAll('.ocu-cart-line-item-wrapper[data-key]');
    if (!wrappers.length) return;

    fetchCartJson().then(function (cart) {
      if (!cart) return;
      var byKey = {};
      cart.items.forEach(function (item) { byKey[item.key] = item; });

      wrappers.forEach(function (wrapper) {
        var key = wrapper.getAttribute('data-key');
        var item = byKey[key];
        if (!item) return;

        var display = computeDisplay(item.final_line_price, rate);
        var formatted = formatMoney(display, CFG.moneyFormat);
        setInnerText(wrapper, '.ocu-cart-line-price-now', formatted);

        if (item.original_line_price !== item.final_line_price) {
          var compareDisplay = computeDisplay(item.original_line_price, rate);
          var compareFormatted = formatMoney(compareDisplay, CFG.moneyFormat);
          setInnerText(wrapper, '.ocu-cart-line-price-was, .ocu-cart-line-price-original', compareFormatted);
        }
      });

      var totalEl = document.querySelector('.ocu-estimated-info__value');
      if (totalEl) {
        var totalDisplay = computeDisplay(cart.total_price, rate);
        totalEl.textContent = formatMoney(totalDisplay, CFG.moneyFormatWithCurrency);
      }
    });
  }

  /* OCU's "You may also like" upsell widget - recommended products not in cart, no
     /cart.js data available. OCU renders these at a fixed tax-EXCLUSIVE price of its
     own regardless of the shop's tax settings. We treat that first-seen number as the
     excl-VAT baseline (cached per-element via data-cj-ocu-base, in cents, parsed from
     the text OCU itself rendered) and recompute from that baseline every time, so
     toggling back and forth is always exact with no drift. */
  var OCU_PRICE_RE = /^(\d[\d\s]*[.,]\d{2})\s*(.*)$/;

  function parseOcuMoney(text) {
    var m = text.match(OCU_PRICE_RE);
    if (!m) return null;
    var numStr = m[1].replace(/\s/g, '').replace(',', '.');
    var num = parseFloat(numStr);
    if (isNaN(num)) return null;
    return { cents: Math.round(num * 100), suffix: m[2] };
  }

  function formatOcuMoney(cents, suffix) {
    var str = (cents / 100).toFixed(2);
    return suffix ? str + ' ' + suffix : str;
  }

  function applyOcuWidgetPrices(rate) {
    var widgets = document.querySelectorAll('.ocu-widget');
    if (!widgets.length) return;

    widgets.forEach(function (widget) {
      var leafEls = Array.from(widget.querySelectorAll('*')).filter(function (el) {
        return el.children.length === 0 && el.textContent && el.textContent.trim().length > 0;
      });

      leafEls.forEach(function (el) {
        var baseAttr = el.getAttribute('data-cj-ocu-base');
        var suffixAttr = el.getAttribute('data-cj-ocu-suffix');
        var baseCents, suffix;

        if (baseAttr !== null) {
          baseCents = parseInt(baseAttr, 10);
          suffix = suffixAttr || '';
        } else {
          var parsed = parseOcuMoney(el.textContent.trim());
          if (!parsed) return; // not a price-looking element, skip
          baseCents = parsed.cents;
          suffix = parsed.suffix;
          el.setAttribute('data-cj-ocu-base', String(baseCents));
          el.setAttribute('data-cj-ocu-suffix', suffix);
        }

        // OCU's own number is always the excl-VAT baseline; only the "incl" choice needs
        // a change (multiply up). "excl" (or no explicit choice) = OCU's number as-is.
        var choice = getChoice() || 'excl';
        var displayCents = choice === 'incl' ? Math.round(baseCents * (1 + rate / 100)) : baseCents;
        el.textContent = formatOcuMoney(displayCents, suffix);
      });
    });
  }

  /* Dawn's static "Taxes included" / "Duties and taxes included" note under the PDP
     price (sections/main-product.liquid, .cj-vat-tax-note span). It's server-rendered
     unconditionally whenever the shop has taxes-included pricing on, which is always
     true here - so it contradicts the page when the customer has chosen to view
     exclusive-of-VAT prices. Hide it in that case; show it (the default, matching the
     shop's real checkout behavior) for "incl" or when no choice has been made yet. */
  function applyTaxNoteVisibility() {
    var choice = getChoice() || 'incl';
    document.querySelectorAll('.cj-vat-tax-note').forEach(function (el) {
      el.style.display = choice === 'excl' ? 'none' : '';
    });
  }

  /* Read-only: derive the currently-active reward tier's real (tax-inclusive) threshold
     from OCU's own live fill-percentage plus the real cart total. Never writes anything -
     safe to call as often as needed. Returns null if no tier is currently "in progress"
     (e.g. all tiers complete, or reward bar isn't present). */
  function deriveRewardThresholdCents(cart) {
    var bar = document.querySelector('.ocu-reward-bar');
    if (!bar) return null;
    var tiers = bar.querySelectorAll('.ocu-reward-bar-tier');
    for (var i = 0; i < tiers.length; i++) {
      var style = tiers[i].getAttribute('style') || '';
      var m = style.match(/:\s*([\d.]+)%/);
      if (!m) continue;
      var pct = parseFloat(m[1]);
      if (pct > 0 && pct < 100) {
        return Math.round((cart.total_price * 100) / pct);
      }
    }
    return null;
  }

  /* Our OWN clarifying note, inserted as a sibling next to OCU's reward bar title -
     never mutates OCU's own node, so it can never break OCU's reactivity or get stuck.
     Recreated/updated fresh every cycle; if OCU wipes its own subtree on a re-render our
     note gets removed along with it, and we just re-insert it next cycle - fully safe. */
  function applyOcuRewardNote(rate) {
    var choice = getChoice();
    var wrapper = document.querySelector('.ocu-reward-bar-message-wrapper');

    if (!wrapper || choice !== 'excl') {
      var stale = document.querySelector('.cj-vat-reward-note');
      if (stale) stale.remove();
      return;
    }

    fetchCartJson().then(function (cart) {
      if (!cart) return;
      var thresholdCents = deriveRewardThresholdCents(cart);

      // re-check wrapper is still there (may have been replaced while we awaited the fetch)
      wrapper = document.querySelector('.ocu-reward-bar-message-wrapper');
      if (!wrapper) return;

      if (thresholdCents == null) {
        var doneNote = wrapper.querySelector('.cj-vat-reward-note');
        if (doneNote) doneNote.remove();
        return;
      }

      var displayedTotal = computeDisplay(cart.total_price, rate);
      var displayedThreshold = computeDisplay(thresholdCents, rate);
      var remainingCents = Math.max(0, displayedThreshold - displayedTotal);
      var remainingFormatted = formatMoney(remainingCents, CFG.moneyFormat);
      var newText = remainingCents > 0
        ? '(' + (I18N.vatExclShort || 'Excl. VAT') + ': ' + remainingFormatted + ')'
        : '(' + (I18N.vatExclShort || 'Excl. VAT') + ')';

      var note = wrapper.querySelector('.cj-vat-reward-note');
      if (!note) {
        note = document.createElement('div');
        note.className = 'cj-vat-reward-note';
        note.style.fontSize = '1.1rem';
        note.style.opacity = '0.75';
        note.style.marginTop = '0.2rem';
        wrapper.appendChild(note);
      }
      if (note.textContent !== newText) {
        note.textContent = newText;
      }
    });
  }

  function applyVatDisplay() {
    if (!getChoice()) return; // no preference chosen yet - leave server-rendered prices untouched
    getRates().then(function (rateTable) {
      var country = getCountry();
      var rate = rateTable[country];
      if (rate === undefined || rate === null) return; // unknown country, don't touch prices

      rewriteDataPriceElements(rate);
      applyOcuCartPrices(rate);
      applyOcuWidgetPrices(rate);
      applyTaxNoteVisibility();
      applyOcuRewardNote(rate);
    });
  }

  /* Populates the country <select> options, localized to CFG.lang, and preselects
     whichever country we already believe the customer is in: their own saved choice
     if they've picked one before (STORAGE_COUNTRY), otherwise Shopify's own geo-
     detected market (CFG.defaultCountry, from localization.country.iso_code) - see
     getCountry(). This runs once per page load (dataset.populated guard), so it
     reflects whatever CFG.lang/defaultCountry were at load time. */
  function populateCountrySelect(select, rateTable) {
    if (!select || select.dataset.populated) return;
    var current = getCountry();
    var names = getCountryNames();
    Object.keys(rateTable).sort().forEach(function (code) {
      var opt = document.createElement('option');
      opt.value = code;
      opt.textContent = (names[code] || code) + ' (' + rateTable[code] + '%)';
      if (code === current) opt.selected = true;
      select.appendChild(opt);
    });
    select.dataset.populated = 'true';
  }

  /* Populates the popup modal's static copy (title, description, country-select label,
     choice-button labels, and the "What is this?" info link) once from CFG.i18n/infoUrl.
     This runs regardless of whether a choice has been made yet, since the modal needs
     its text ready before it's ever shown. The info link only shows if both a translated
     caption AND a resolved page URL exist (infoUrl comes from theme.liquid's
     pages['understanding-vat'].url - if that page were ever deleted/renamed, the URL
     would come back blank and we hide the link rather than show a dead one). */
  function applyI18n() {
    var title = document.getElementById('cj-vat-modal-title');
    var text = document.getElementById('cj-vat-modal-text');
    var countryLabel = document.getElementById('cj-vat-country-label');
    var inclLabel = document.getElementById('cj-vat-choice-incl-label');
    var inclSub = document.getElementById('cj-vat-choice-incl-sub');
    var exclLabel = document.getElementById('cj-vat-choice-excl-label');
    var exclSub = document.getElementById('cj-vat-choice-excl-sub');
    var infoLink = document.getElementById('cj-vat-info-link');

    if (title) title.textContent = I18N.modalTitle || 'How would you like to see prices?';
    if (text) text.textContent = I18N.modalText || '';
    if (countryLabel) countryLabel.textContent = I18N.countryLabel || 'Country';
    if (inclLabel) inclLabel.textContent = I18N.customerPrivate || 'Private customer';
    if (inclSub) inclSub.textContent = I18N.vatInclShort || 'Incl. VAT';
    if (exclLabel) exclLabel.textContent = I18N.customerBusiness || 'Business customer';
    if (exclSub) exclSub.textContent = I18N.vatExclShort || 'Excl. VAT';
    if (infoLink) {
      if (CFG.infoUrl && I18N.whatIsThis) {
        infoLink.textContent = I18N.whatIsThis;
        infoLink.href = CFG.infoUrl;
        infoLink.hidden = false;
      } else {
        infoLink.hidden = true;
      }
    }
  }

  /* Header toggle label: two-line "customer type" + "VAT mode" layout, text sourced
     entirely from CFG.i18n (resolved server-side per request.locale.iso_code, with the
     Swedish market/country override baked in there already - no branching needed here).
     Updates EVERY element matching .cj-vat-header-toggle (there are two: the desktop
     header instance and the mobile drawer instance - see sections/header.liquid and
     snippets/header-drawer.liquid), so both always show the same choice regardless of
     which one the customer last interacted with. */
  function updatePillLabel() {
    var pills = document.querySelectorAll('.cj-vat-header-toggle');
    if (!pills.length) return;

    var choice = getChoice();

    pills.forEach(function (pill) {
      var customerLabel = pill.querySelector('.cj-vat-header-toggle__customer');
      var divider = pill.querySelector('.cj-vat-header-toggle__divider');
      var vatLabel = pill.querySelector('.cj-vat-header-toggle__vat');
      if (!vatLabel) return;

      if (!choice) { pill.hidden = true; return; }
      pill.hidden = false;

      if (customerLabel) {
        customerLabel.hidden = false;
        customerLabel.textContent = choice === 'incl'
          ? (I18N.customerPrivate || 'Private customer')
          : (I18N.customerBusiness || 'Business customer');
      }
      if (divider) divider.hidden = false;
      vatLabel.textContent = choice === 'incl'
        ? (I18N.pillVatIncl || 'Prices incl. VAT')
        : (I18N.pillVatExcl || 'Prices excl. VAT');
    });
  }

  function openModal() {
    var modal = document.getElementById('cj-vat-modal');
    if (modal) modal.hidden = false;
  }
  function closeModal() {
    var modal = document.getElementById('cj-vat-modal');
    if (modal) modal.hidden = true;
  }

  /* Fired on document whenever the customer's VAT choice/country actually changes,
     OR once right after the initial rates/choice state has been resolved on page
     load - lets independent widgets that compute their own prices client-side (e.g.
     bundle-builder.js) re-render both on that first load AND on later changes,
     without vat-toggle.js needing any knowledge of their specific markup. */
  function notifyVatChanged() {
    document.dispatchEvent(new CustomEvent('cj-vat:changed'));
  }

  function init() {
    applyI18n();

    // Show the modal (if the customer hasn't chosen yet) and wire up all its
    // controls IMMEDIATELY on DOMContentLoaded, without waiting for the
    // vat-rates.json fetch below. Previously this whole block lived inside
    // getRates().then(...), so the popup only appeared once that fetch
    // resolved - a beat after the page had already rendered, which read as
    // a jarring "jump" (especially on mobile, where a fixed full-screen
    // overlay suddenly covering an already-settled page feels broken).
    // The modal's copy (applyI18n above) and choice buttons don't need the
    // rate table at all - only price rewriting (applyVatDisplay) and the
    // country <select>'s options do, so those alone stay gated on the fetch.
    var select = document.getElementById('cj-vat-country');

    if (!getChoice()) {
      openModal();
    }
    updatePillLabel();

    document.querySelectorAll('.cj-vat-modal__choice-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var choice = btn.getAttribute('data-choice');
        var country = select ? select.value : getCountry();
        setPref(choice, country);
        closeModal();
        updatePillLabel();
        applyVatDisplay();
        notifyVatChanged();
      });
    });

    var closeBtn = document.querySelector('.cj-vat-modal__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        if (!getChoice()) setPref('incl', select ? select.value : getCountry());
        closeModal();
        updatePillLabel();
        applyVatDisplay();
        notifyVatChanged();
      });
    }

    var overlay = document.querySelector('.cj-vat-modal__overlay');
    if (overlay && closeBtn) {
      overlay.addEventListener('click', function () { closeBtn.click(); });
    }

    // Both header-toggle instances (desktop icons row + mobile drawer) open the
    // same shared modal. If the click came from the mobile drawer instance, also
    // close the drawer itself first so the modal isn't fighting the drawer's own
    // overlay for attention.
    document.querySelectorAll('.cj-vat-header-toggle').forEach(function (pill) {
      pill.addEventListener('click', function () {
        var drawerDetails = document.getElementById('Details-menu-drawer-container');
        if (drawerDetails && drawerDetails.hasAttribute('open')) {
          drawerDetails.removeAttribute('open');
        }
        openModal();
      });
    });

    getRates().then(function (rateTable) {
      populateCountrySelect(select, rateTable);
      applyVatDisplay();
      // Rates just finished loading for the first time (async fetch) - any
      // widget that computed its own display prices earlier (e.g. bundle-
      // builder.js constructed before this promise resolved) may have done so
      // with no rate available yet and silently fallen back to raw cents.
      // Signal now so it can recompute with the real rate in hand.
      notifyVatChanged();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-run after cart drawer / AJAX DOM swaps. Dawn replaces innerHTML with fresh
     server-rendered sections on cart update (data-raw-price attributes come back
     too), and OCU's Vue-based cart drawer re-renders its own DOM on every change -
     both cases just mean "something changed, re-scan and re-fetch /cart.js". */
  var debounceTimer;
  var observer = new MutationObserver(function (mutations) {
    var relevant = false;
    for (var i = 0; i < mutations.length; i++) {
      if (mutations[i].addedNodes && mutations[i].addedNodes.length) { relevant = true; break; }
    }
    if (!relevant) return;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(applyVatDisplay, 60);
  });
  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  }

  window.CJVatToggle = {
    applyVatDisplay: applyVatDisplay,
    openModal: openModal,
    computeDisplay: publicComputeDisplay,
    formatMoney: formatMoney,
    getChoice: getChoice
  };
})();
